import { motion } from 'framer-motion'
import { Rocket, Shield, Gift } from 'lucide-react'
import { useLanguage } from '@/context/LanguageContext'
import { useAnimateOnScroll } from '@/hooks/useAnimateOnScroll'
import { EmailForm } from '@/components/ui/EmailForm'

export function FinalCTA() {
  const { t } = useLanguage()
  const { ref, isVisible } = useAnimateOnScroll({ threshold: 0.2 })

  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-midnight-950 to-midnight-900" />

      {/* Multiple Glow Effects */}
      <div
        className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] opacity-30"
        style={{
          background: 'radial-gradient(circle, rgba(247, 208, 70, 0.2) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />
      <div
        className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[400px] h-[400px] opacity-20"
        style={{
          background: 'radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center"
        >
          {/* Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={isVisible ? { scale: 1 } : {}}
            transition={{ type: 'spring', stiffness: 500, damping: 30, delay: 0.2 }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-gold-500/20 to-gold-600/20 mb-8"
          >
            <Rocket className="w-10 h-10 text-gold-400" />
          </motion.div>

          {/* Headline */}
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-100 mb-4">
            {t.finalCta.headline}
          </h2>
          <p className="text-lg sm:text-xl text-slate-400 mb-10">
            {t.finalCta.subheadline}
          </p>

          {/* Email Form */}
          <div className="max-w-lg mx-auto mb-8">
            <EmailForm variant="hero" />
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-profit-500" />
              <span>{t.finalCta.guarantee}</span>
            </div>
            <div className="flex items-center gap-2">
              <Gift className="w-4 h-4 text-gold-400" />
              <span>Founding member benefits</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
